import { Column, Line, Pie } from '@ant-design/plots';
import { Typography } from 'antd';

export interface AiChartSpec {
  chartType: 'line' | 'column' | 'pie';
  title: string;
  data: Array<{ x: string; y: number; series?: string }>;
}

const MAX_CHART_POINTS = 100;

/**
 * 解析后端 ai_message.charts（JSON 数组字符串）/ SSE chart 事件（单个 spec JSON）。
 * spec 属于不可信数据（模型产出 + 历史库数据），逐字段运行时校验/规整，
 * 畸形字段不允许击穿渲染，数据点截断防 canvas 压力。
 */
export function parseChartSpecs(raw?: string | null): AiChartSpec[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    const list = Array.isArray(parsed) ? parsed : [parsed];
    const specs: AiChartSpec[] = [];
    for (const item of list) {
      if (!item || !['line', 'column', 'pie'].includes(item.chartType) || !Array.isArray(item.data)) {
        continue;
      }
      const data: AiChartSpec['data'] = [];
      for (const point of item.data.slice(0, MAX_CHART_POINTS)) {
        const y = Number(point?.y);
        if (!point || point.x === undefined || point.x === null || !Number.isFinite(y)) {
          continue;
        }
        data.push({
          x: String(point.x),
          y,
          ...(point.series !== undefined && point.series !== null
            ? { series: String(point.series) }
            : {}),
        });
      }
      if (!data.length) continue;
      specs.push({
        chartType: item.chartType,
        title: typeof item.title === 'string' ? item.title : String(item.title ?? ''),
        data,
      });
    }
    return specs;
  } catch {
    return [];
  }
}

const ChartRenderer = ({ spec }: { spec: AiChartSpec }) => {
  const hasSeries = spec.data.some((d) => d.series);

  return (
    <div style={{ marginTop: 12, background: '#fff', borderRadius: 8, padding: 12 }}>
      <Typography.Text strong>{spec.title}</Typography.Text>
      <div style={{ marginTop: 8 }}>
        {spec.chartType === 'line' && (
          <Line
            height={260}
            data={spec.data}
            xField="x"
            yField="y"
            seriesField={hasSeries ? 'series' : undefined}
            padding="auto"
          />
        )}
        {spec.chartType === 'column' && (
          <Column
            height={260}
            data={spec.data}
            xField="x"
            yField="y"
            seriesField={hasSeries ? 'series' : undefined}
            isGroup={hasSeries}
            padding="auto"
          />
        )}
        {spec.chartType === 'pie' && (
          <Pie
            height={260}
            data={spec.data}
            angleField="y"
            colorField="x"
            radius={1}
            appendPadding={10}
            label={{ type: 'outer' }}
          />
        )}
      </div>
    </div>
  );
};

export default ChartRenderer;
